import {ProgramPaths} from "@/enum/ProgramPaths";
import {LisansDurum} from "@/utils/lisansKisit";
import {urunAnahtari, urunBilgisi} from "@/utils/urunKatalogu";

/**
 * DEV-2113 — lisans kaynaklı 403'te gösterilen uyarının içeriği.
 *
 * Metin sunucu mesajından DEĞİL buradan gelir: sunucu mesajı tek tip ve genel
 * ("Bu ürün için geçerli bir lisansınız bulunmuyor."); kullanıcıya hangi ürünün,
 * neden ve ne kadar süreyle kapalı olduğunu söylemek ekranın işi. Ürünü sunucunun
 * `urunler` alanından okur (kapının gerçekten sorduğu ürün); bulunulan sayfanın
 * ürününden tahmin etmez.
 */
export interface LisansUyariIcerigi {
    ikon: "warning" | "info";
    baslik: string;
    metin: string;
    not: string | null;
    eylem: {etiket: string; hedef: {path: string; query?: Record<string, string>}} | null;
}

/**
 * Kapının sorduğu ürünlerden engele yol açanı seçer: kısıtlı dönem engelinde
 * kısıtlıdaki ürün, lisans engelinde süresi bitmiş ya da lisansı olmayan ürün.
 * Tek ürün sorulduysa o ürün; seçim yapılamıyorsa null (metin genel kalır).
 */
export function engelleyenUrun(
    tip: string,
    urunler: number[],
    urunDurumlari: Record<string, LisansDurum> | null | undefined
): number | null {
    const durumlar = urunDurumlari ?? {};
    const aranan: LisansDurum[] = tip === "license_grace_readonly" ? ["kisitli"] : ["sure_bitti", "yok"];
    const uyan = urunler.filter((u) => aranan.includes(durumlar[String(u)]));
    if (uyan.length > 0) return uyan[0];
    return urunler.length === 1 ? urunler[0] : null;
}

export function lisansUyariIcerigi(
    tip: string,
    urun: number | null,
    durum: LisansDurum | null,
    kalanGun: number | null
): LisansUyariIcerigi {
    const bilgi = urunBilgisi(urun);
    const ad = bilgi ? bilgi.ad : null;
    const lisansEkrani = (etiket: string) => ({
        etiket,
        hedef: bilgi
            ? {path: ProgramPaths.lisansDurumu, query: {urun: urunAnahtari(bilgi)}}
            : {path: ProgramPaths.lisansDurumu},
    });

    if (tip === "feature_disabled") {
        return {
            ikon: "info",
            baslik: "Bu özellik büronuzda aktif değil",
            metin: "Bu özellik şu anda yalnızca belirli bürolarda kullanılabiliyor. Bilgi almak için destek ekibimizle iletişime geçebilirsiniz.",
            not: null,
            eylem: null,
        };
    }

    if (tip === "license_grace_readonly") {
        return {
            ikon: "warning",
            baslik: ad ? `${ad} kısıtlı modda` : "Kısıtlı moddasınız",
            metin: `Kullanım süreniz dolduğu için ${ad ?? "ürün"} kısıtlı modda çalışıyor. Kayıtlarınızı görüntüleyebilirsiniz; bu işlem kısıtlı modda yapılamaz.`,
            not: kalanGun !== null ? `Kısıtlı mod ${kalanGun} gün sonra sona erecek.` : null,
            eylem: lisansEkrani("Süreyi uzat"),
        };
    }

    if (tip === "license_none") {
        return {
            ikon: "warning",
            baslik: "Hesabınızda lisans bulunmuyor",
            metin: "Bu hesaba tanımlı bir ürün lisansı yok. Bir paket satın alarak kullanmaya başlayabilirsiniz.",
            not: null,
            eylem: lisansEkrani("Paketleri incele"),
        };
    }

    if (durum === "yok") {
        return {
            ikon: "warning",
            baslik: ad ? `${ad} lisansınız bulunmuyor` : "Bu ürün için lisansınız bulunmuyor",
            metin: ad
                ? `Bu işlem için ${ad} lisansı gerekiyor. Paket satın alarak hemen kullanmaya başlayabilirsiniz.`
                : "Bu işlem için ilgili ürünün lisansı gerekiyor.",
            not: null,
            eylem: lisansEkrani("Paket satın al"),
        };
    }

    return {
        ikon: "warning",
        baslik: ad ? `${ad} kullanım süreniz doldu` : "Kullanım süreniz doldu",
        metin: ad
            ? `Bu işlem için geçerli bir ${ad} lisansı gerekiyor. Süreyi uzatarak kaldığınız yerden devam edebilirsiniz.`
            : "Bu işlem için geçerli bir lisans gerekiyor. Süreyi uzatarak kaldığınız yerden devam edebilirsiniz.",
        not: null,
        eylem: lisansEkrani("Süreyi uzat"),
    };
}
